import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  ApplicantDetail,
  ApplicantListResponse,
  ApplicantNotesInput,
  ApplicantStatusInput,
  ApplicationStatusValue,
  HireApplicantInput,
  HireApplicantResult,
} from '@shared/schemas/applicant';
import type { RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/applicants. ROADMAP.md §9.10. */

const ROOT = ['admin', 'applicants'] as const;

/**
 * The badge tone per state — the one mapping, so no screen invents its own.
 *
 * `not_hired` is `cancelled`-toned rather than red: not hiring somebody is a
 * normal answer to a normal question, and a red badge on a real person's
 * application reads as a warning about them.
 */
export const APPLICANT_TONES: Record<ApplicationStatusValue, StatusTone> = {
  new: 'pending',
  reviewing: 'scheduled',
  interviewing: 'in-progress',
  hired: 'completed',
  not_hired: 'cancelled',
};

/**
 * Hiring creates an **employee**, so the employees and scheduling caches are
 * stale too — otherwise the person just hired is missing from the employee list
 * until a reload, which reads as the hire not having worked.
 */
function invalidate(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ROOT }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] }),
  ]).then(() => undefined);
}

export interface ApplicantListParams {
  sort: RecordSort;
  page: number;
  status: ApplicationStatusValue | 'all';
  search: string;
  positionId: string;
}

export function useApplicants(params: ApplicantListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ApplicantListResponse>(
        `/api/admin/applicants${queryString({
          page: params.page,
          sort: params.sort,
          status: params.status,
          search: params.search === '' ? undefined : params.search,
          positionId: params.positionId === '' ? undefined : params.positionId,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useApplicant(id: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, 'detail', id],
    queryFn: () => api.get<ApplicantDetail>(`/api/admin/applicants/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

/** Only positions somebody has actually applied for — an empty filter is noise. */
export function useApplicantPositions() {
  return useQuery({
    queryKey: [...ROOT, 'positions'],
    queryFn: () =>
      api.get<{ positions: { id: string; name: string }[] }>('/api/admin/applicants/positions'),
  });
}

export function useSetApplicantStatus(applicationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplicantStatusInput) =>
      api.post<ApplicantDetail>(`/api/admin/applicants/${applicationId}/status`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useSetApplicantNotes(applicationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplicantNotesInput) =>
      api.patch<ApplicantDetail>(`/api/admin/applicants/${applicationId}/notes`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useHireApplicant(applicationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: HireApplicantInput) =>
      api.post<HireApplicantResult>(`/api/admin/applicants/${applicationId}/hire`, input),
    onSuccess: () => invalidate(queryClient),
  });
}
